/**
 * Newsletter Utility Functions
 * Client-side functions for fetching and displaying newsletters
 */

import { supabase, isSupabaseConfigured } from './supabase'

/**
 * Fetch all published newsletter editions
 * @param {number} limit - Maximum number of editions to fetch
 * @returns {Promise<Array>} List of newsletter editions
 */
export async function getNewsletterEditions(limit = 20) {
    if (!isSupabaseConfigured()) {
        return []
    }

    try {
        const { data, error } = await supabase
            .from('newsletter_editions')
            .select('id, title, subject, content_preview, published_at, stats_summary')
            .eq('sent_status', 'sent')
            .order('published_at', { ascending: false })
            .limit(limit)

        if (error) throw error
        return data || []
    } catch (error) {
        console.error('Error fetching newsletters:', error)
        return []
    }
}

/**
 * Fetch a single newsletter edition by ID
 * @param {string} id - Newsletter edition ID
 * @returns {Promise<Object|null>} Newsletter edition or null
 */
export async function getNewsletterById(id) {
    if (!isSupabaseConfigured() || !id) {
        return null
    }

    try {
        const { data, error } = await supabase
            .from('newsletter_editions')
            .select('*')
            .eq('id', id)
            .eq('sent_status', 'sent')
            .single()

        if (error) throw error
        return data
    } catch (error) {
        console.error('Error fetching newsletter:', error)
        return null
    }
}

/**
 * Subscribe an email to the newsletter
 * @param {string} email - Email address to subscribe
 * @param {string} source - Source of subscription (e.g., 'landing_page')
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function subscribeToNewsletter(email, source = 'landing_page') {
    if (!isSupabaseConfigured()) {
        return { success: false, error: 'Database not configured' }
    }

    if (!email || !email.includes('@')) {
        return { success: false, error: 'Invalid email address' }
    }

    try {
        const { error } = await supabase
            .from('newsletter_subscribers')
            .insert({
                email: email.toLowerCase().trim(),
                source,
                status: 'active'
            })

        if (error) {
            if (error.code === '23505') {
                // Already subscribed - treat as success
                return { success: true, alreadySubscribed: true }
            }
            throw error
        }

        return { success: true }
    } catch (error) {
        console.error('Subscription error:', error)
        return { success: false, error: 'Failed to subscribe. Please try again.' }
    }
}

/**
 * Unsubscribe an email from the newsletter
 * @param {string} email - Email address to unsubscribe
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function unsubscribeFromNewsletter(email) {
    if (!isSupabaseConfigured() || !email) {
        return { success: false, error: 'Invalid request' }
    }

    try {
        const { error } = await supabase
            .from('newsletter_subscribers')
            .update({ status: 'unsubscribed' })
            .eq('email', email.toLowerCase().trim())

        if (error) throw error
        return { success: true }
    } catch (error) {
        console.error('Unsubscribe error:', error)
        return { success: false, error: 'Failed to unsubscribe. Please try again.' }
    }
}

/**
 * Format date for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
export function formatNewsletterDate(dateString) {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })
}
